import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["disabled", "itemComponent", "buttonUse", "placeholder"];
import { useCallback, useMemo, memo, useState, useEffect } from 'react';
import PortalIdParser from 'PortalIdParser';
import { UserAttributesClient } from 'frontend-preferences-client';
import { FloatingAlertList } from 'foundations-components/transitional/components/FloatingAlertList';
import { Button } from 'foundations-components/transitional/components/Button';
import { Link } from 'foundations-components/transitional/components/Link';
import { ButtonWrapper } from 'foundations-components/transitional/components/ButtonWrapper';
import { View } from 'foundations-components/components/View';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import useCampaignSelect from '../hooks/useCampaignSelect';
import useTrackCampaignSelectInteraction from '../hooks/useTrackCampaignSelectInteraction';
import useShouldDisplayTourLinkOnCampaignSelect from '../hooks/useShouldDisplayTourLinkOnCampaignSelect';
import CampaignEditPanel from './panels/CampaignEditPanel';
import { DropdownFooter } from './campaignSelect/helperComponents';
import CampaignStudioTile from './campaignSelect/CampaignStudioTile';
import CampaignSelectWithAutoSelectPopover from './CampaignSelectWithAutoSelectPopover';
import CampaignSelectItemComponent from './CampaignSelectItemComponent';
import { StandaloneUsageTracker } from '../utils/usageTracker';
import { Text } from 'foundations-components/components/Text';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { useEmbeddedEditorContext } from '../embedded/EmbeddedEditorContext';
import MiniCreateCampaignPanel from './panels/MiniCreateCampaignPanel';
import MarketingStudioOptinWrapper, { useStudioOptinContext } from './studio/MarketingStudioOptinWrapper';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
export function CampaignSelectComponent({
  assetObjectType,
  autoLoad = false,
  'aria-labelledby': ariaLabelledBy,
  canCreateCampaigns = false,
  onClickCreate,
  multi = false,
  withCampaignAutoSelectPopover = false,
  withEmptyState = false,
  disabledTooltip,
  uiSelectProps = {},
  onChange,
  value,
  applicationId,
  disabledOptionsByName,
  enableTracking = false,
  numberOfRecentlyUsed,
  onCurrentValueNotFound,
  pageLimit,
  recentlyUsedCacheGracePeriod,
  onCampaignCreateRef,
  objectBuilderUiProps,
  disabledTooltipPlacement,
  isFilter,
  numberOfObjectsToAssociate
}) {
  const {
      disabled,
      itemComponent,
      buttonUse,
      placeholder
    } = uiSelectProps,
    rest = _objectWithoutPropertiesLoose(uiSelectProps, _excluded);
  const {
    alertStore,
    createdCampaigns,
    handleCampaignAutoSelectChange,
    handleSelectedOptionChange,
    hasDismissedPopover,
    hasOpened,
    loadOptions,
    onCampaignCreated,
    options,
    placeholderToDisplay,
    setHasDismissedPopover,
    setHasOpened,
    setShowCreatePanel,
    setValueFromAttributes,
    showCreatePanel,
    valueToDisplay,
    showEmptyState,
    hasLoadedFullOptions
  } = useCampaignSelect({
    applicationId,
    disabledOptionsByName,
    multi,
    numberOfRecentlyUsed,
    onCampaignCreateRef,
    onChange,
    onCurrentValueNotFound,
    pageLimit,
    placeholder,
    recentlyUsedCacheGracePeriod,
    value,
    assetObjectType
  });
  const [seenMarketingStudio, setSeenMarketingStudio] = useState(false);
  const {
    hasAccessToMarketingStudio
  } = useStudioOptinContext();
  const {
    isInsideMarketingStudio
  } = useEmbeddedEditorContext();
  useEffect(() => {
    const getTileSeen = async () => {
      var _JSON$parse;
      const userAttributesClient = UserAttributesClient.forCaller('campaigns-lib');
      const seenValue = await userAttributesClient.fetch('Campaign:HasSeenStudio', 'false');
      setSeenMarketingStudio((_JSON$parse = JSON.parse(seenValue)) !== null && _JSON$parse !== void 0 ? _JSON$parse : false);
    };
    void getTileSeen();
  }, []);
  const {
    handleCampaignAutoSelectChangeWithTracking,
    handleSelectedOptionChangeWithTracking
  } = useTrackCampaignSelectInteraction({
    applicationId,
    enableTracking,
    handleCampaignAutoSelectChange,
    handleSelectedOptionChange,
    assetObjectType,
    numberOfObjectsToAssociate
  });
  const defaultItemComponent = useCallback(props => /*#__PURE__*/_jsx(CampaignSelectItemComponent, Object.assign({}, props, {
    buttonUse: buttonUse,
    multi: multi,
    "data-test-id": "campaign-select-item-component"
  })), [buttonUse, multi]);
  const portalId = useMemo(() => PortalIdParser.get(), []);
  const shouldDisplayTourLinkOnCampaignSelect = useShouldDisplayTourLinkOnCampaignSelect();
  const tourLinkTracker = useCallback(() => {
    StandaloneUsageTracker.track('Campaigns Lib', 'Campaign Select Interaction', {
      action: 'onboarding tour link clicked',
      assetType: assetObjectType
    });
  }, [assetObjectType]);
  const hasSelectedCampaign = !!valueToDisplay;
  const hasSeenMarketingStudioAndHasSelectedCampaign = seenMarketingStudio && valueToDisplay;
  return /*#__PURE__*/_jsx(Tooltip, {
    disabled: !disabled,
    title: disabledTooltip,
    placement: disabledTooltipPlacement,
    "data-test-id": "campaigns-select-disabled-tooltip",
    children: /*#__PURE__*/_jsxs(Text, {
      as: "span",
      "data-test-id": "campaign-select-component",
      visuallyHidden: withEmptyState && !hasLoadedFullOptions,
      children: [withEmptyState && showEmptyState && canCreateCampaigns && !isInsideMarketingStudio ? /*#__PURE__*/_jsxs(ButtonWrapper, {
        children: [/*#__PURE__*/_jsx(Button, {
          use: "tertiary",
          "data-test-id": "campaigns-select-empty-state",
          onClick: () => {
            setShowCreatePanel(true);
            setHasOpened(true);
          },
          marginBlockStart: "200",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "campaignSelect.emptyStateCreateCampaign"
          })
        }), shouldDisplayTourLinkOnCampaignSelect && /*#__PURE__*/_jsx(Link, {
          "data-test-id": "campaigns-select-onboarding-tour-link",
          external: true,
          onClick: tourLinkTracker,
          href: `/marketing/${portalId}/campaigns`,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "campaignSelect.emptyStateTakeOnboardingTour"
          })
        })]
      }) : /*#__PURE__*/_jsx(CampaignSelectWithAutoSelectPopover, {
        uiSelectProps: Object.assign({
          'aria-labelledby': ariaLabelledBy
        }, rest, {
          readOnly: isInsideMarketingStudio,
          clearable: true,
          dropdownFooter: !canCreateCampaigns ? null : /*#__PURE__*/_jsx(DropdownFooter, {
            onClickCreate: onClickCreate,
            setShowCreatePanel: setShowCreatePanel
          }),
          itemComponent: itemComponent || defaultItemComponent,
          loadOptions,
          options,
          minimumSearchCount: 1,
          onSelectedOptionChange: handleSelectedOptionChangeWithTracking,
          placeholder: placeholderToDisplay,
          searchable: true,
          value: valueToDisplay,
          autoload: hasOpened || multi || autoLoad,
          buttonUse,
          onOpenChange: () => {
            if (!hasOpened) {
              setHasOpened(true);
            }
          },
          multi,
          disabled
        }),
        withCampaignAutoSelectPopover: withCampaignAutoSelectPopover,
        forceClose: hasOpened,
        setValueFromAttributes: setValueFromAttributes,
        onCampaignAutoSelectChange: handleCampaignAutoSelectChangeWithTracking,
        valueFromProps: value,
        hasDismissedPopover: hasDismissedPopover,
        setHasDismissedPopover: setHasDismissedPopover
      }, createdCampaigns.length), hasAccessToMarketingStudio && !isFilter && !isInsideMarketingStudio && hasSeenMarketingStudioAndHasSelectedCampaign && /*#__PURE__*/_jsx(Text, {
        typography: "detail-100",
        as: "p",
        color: "text-core-subtle",
        paddingBlockStart: "100",
        paddingBlockEnd: "0",
        children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "campaignSelect.seenStudio.formControlSubline_jsx",
          elements: {
            Link
          },
          options: {
            external: true,
            href: `/marketing/${portalId}/studio/${Array.isArray(valueToDisplay) ? valueToDisplay[0] : valueToDisplay}`,
            target: '_blank',
            underline: true,
            typography: 'detail-100'
          }
        })
      }), hasSelectedCampaign && hasAccessToMarketingStudio && !isFilter && !seenMarketingStudio && !isInsideMarketingStudio && /*#__PURE__*/_jsx(View, {
        marginBlockStart: "400",
        children: /*#__PURE__*/_jsx(CampaignStudioTile, {
          portalId: portalId,
          valueToDisplay: valueToDisplay
        }, valueToDisplay.toString())
      }), showCreatePanel && /*#__PURE__*/_jsxs(_Fragment, {
        children: [hasAccessToMarketingStudio && !isInsideMarketingStudio ? /*#__PURE__*/_jsx(MiniCreateCampaignPanel, {
          onClose: () => setShowCreatePanel(false),
          onCreated: campaign => onCampaignCreated(campaign),
          assetObjectType: assetObjectType
        }) : /*#__PURE__*/_jsx(CampaignEditPanel, {
          appName: applicationId || '',
          onClose: response => {
            if (response && response.createdObject) {
              onCampaignCreated(response.createdObject);
            }
            setShowCreatePanel(false);
          },
          use: "create",
          objectBuilderUiProps: objectBuilderUiProps,
          assetObjectType: assetObjectType
        }), /*#__PURE__*/_jsx(FloatingAlertList, {
          alertStore: alertStore
        })]
      })]
    })
  });
}
export const MemoizedCampaignSelectComponent = /*#__PURE__*/memo(CampaignSelectComponent);
const CampaignSelectWithOptinWrapper = props => {
  return /*#__PURE__*/_jsx(MarketingStudioOptinWrapper, {
    shouldRenderOptin: false,
    children: /*#__PURE__*/_jsx(MemoizedCampaignSelectComponent, Object.assign({}, props))
  });
};
export default CampaignSelectWithOptinWrapper;